import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    ChevronLeft,
    Undo,
    Package,
    AlertCircle,
    Save,
    Calendar,
    Hash,
    Loader2,
    MessageSquare
} from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { fetchPOForReturn } from '../apiHelper';
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';

const client = new FastAPIClient()

const ReturnToVendor = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showToast } = useToast()

    // Accept state from navigation
    const { vendor_id, vendor_name, ponumber, po_id } = location.state || {};

    const [loading, setLoading] = useState(true);
    const [poData, setPoData] = useState(null);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Structured state: { [lot_id]: return_quantity }
    const [returnQuantities, setReturnQuantities] = useState({});
    // NEW: State for reasons { [lot_id]: "Reason string" }
    const [returnReasons, setReturnReasons] = useState({});

    useEffect(() => {
        const loadPODetails = async () => {
            // Validation: Check for po_id instead of just ponumber
            if (!po_id) {
                setError("Missing Purchase Order ID. Please go back and select a PO.");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // 1. Call your imported helper
                const data = await fetchPOForReturn(location.state);

                // 2. Set the actual API response to state
                setPoData(data);
            } catch (err) {
                const errorMessage = err.response?.data?.detail || "Error fetching Purchase Order details.";
                console.error(errorMessage, err);
                setError(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        loadPODetails();
    }, [po_id, vendor_id]);

    const handleReasonChange = (lotId, value) => {
        setReturnReasons(prev => ({
            ...prev,
            [lotId]: value
        }));
    };

    const handleQtyChange = (lotId, value, max) => {
        const qty = Math.min(Math.max(0, parseFloat(value) || 0), max);
        setReturnQuantities(prev => ({
            ...prev,
            [lotId]: qty
        }));
    };

    const totals = useMemo(() => {
        let totalValue = 0;
        let totalItems = 0;

        poData?.poitems.forEach(item => {
            item.goodsreceiptitem?.created_lots.forEach(lot => {
                const qty = returnQuantities[lot.lot_id] || 0;
                totalValue += qty * lot.unit_cost;
                totalItems += qty;
            });
        });

        return { totalValue, totalItems };
    }, [poData, returnQuantities]);

    const handleSubmit = async () => {
        // 1. Construct the payload
        const itemsToReturn = Object.entries(returnQuantities)
            .filter(([_, qty]) => qty > 0)
            .map(([lotId, qty]) => ({
                lot_id: parseInt(lotId),
                return_qty: qty,
                reason: returnReasons[lotId] || "No reason provided"
            }));

        if (itemsToReturn.length === 0) return;

        const payload = {
            purchaseorder_id: poData.purchaseorder_id,
            vendor_id: vendor_id,
            document_type: "CREDIT_NOTE",
            items: itemsToReturn
        };

        try {
            setIsSubmitting(true);
            setError(null);

            // 2. API Call
            const response = await client.postData('/vendor/return-to-vendor', payload);
            
            // 3. Success Handling: Clear the form instead of navigating
            // Clear states to reset the UI
            setReturnQuantities({});
            setReturnReasons({});

            // Optional: Trigger a success toast if you have the hook available
            showToast(response.msg || "Return processed successfully", "success");

        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to complete return: ${err.message || err}`;
            console.error("Submission Error:", err);
            setError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return (
        <div className="p-8 text-center flex flex-col items-center gap-2">
            <Loader2 className="animate-spin text-orange-600" size={32} />
            <p>Loading PO Details...</p>
        </div>
    );

    if (error) return (
        <div className="p-8 text-center flex flex-col items-center gap-4">
            <AlertCircle className="text-red-500" size={48} />
            <p className="text-red-600 font-medium">{error}</p>
            <Button onClick={() => navigate(-1)} variant="secondary">Go Back</Button>
        </div>
    );

    return (
        <div className="p-4 max-w-4xl mx-auto bg-gray-50 min-h-screen pb-24">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <Button onClick={() => navigate(-1)} variant="secondary" size="sm">
                    <ChevronLeft size={20} /> Back
                </Button>
                <div className="text-right">
                    <h2 className="text-xl font-bold text-gray-800">Return to Vendor</h2>
                    <p className="text-sm text-gray-500">{ponumber} | {vendor_name}</p>
                </div>
            </div>

            {/* Product Cards */}
            {poData?.poitems.map((item) => (
                <Card key={item.podetail_id} className="mb-6 overflow-hidden border-l-4 border-orange-500">
                    <div className="p-4 bg-white">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h3 className="font-bold text-lg flex items-center gap-2">
                                    <Package className="text-orange-600" size={20} />
                                    {item.catentry.productname}
                                </h3>
                                <p className="text-sm text-gray-500">{item.catentry.partnumber}</p>
                            </div>
                            <div className="text-right">
                                <span className="text-xs font-semibold text-gray-400 uppercase">Total Received</span>
                                <p className="font-bold text-gray-700">{item.qtyreceived}</p>
                            </div>
                        </div>

                        {/* Lot Selection Area */}
                        <div className="space-y-3">
                            <p className="text-xs font-bold text-gray-400 uppercase px-1">Available Lots</p>
                            {item.goodsreceiptitem?.created_lots.map((lot) => (
                                <div key={lot.lot_id} className="p-3 bg-gray-50 rounded-lg border border-gray-100 mb-3">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-3">
                                        <div className="flex-1 grid grid-cols-2 gap-2 text-sm">
                                            <div className="flex items-center gap-1 text-gray-600 font-medium">
                                                <Hash size={14} /> Lot {lot.lot_id}
                                            </div>
                                            <div className="flex items-center gap-1 text-gray-600">
                                                <Calendar size={14} /> {lot.expiration_date || 'No Expiry'}
                                            </div>
                                            <div className="text-gray-500 font-medium">Cost: ₦{parseFloat(lot.unit_cost).toLocaleString()}</div>
                                            <div className="font-bold text-blue-600">Available: {lot.current_quantity}</div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <label className="text-xs font-bold text-gray-500">Return Qty:</label>
                                            <input
                                                type="number"
                                                className="w-20 p-2 border rounded text-center font-bold focus:ring-2 focus:ring-orange-500 outline-none"
                                                value={returnQuantities[lot.lot_id] || ''}
                                                onChange={(e) => handleQtyChange(lot.lot_id, e.target.value, lot.current_quantity)}
                                                placeholder="0"
                                            />
                                        </div>
                                    </div>

                                    {/* NEW: Reason Input Field (Only show if a quantity is entered) */}
                                    {returnQuantities[lot.lot_id] > 0 && (
                                        <Input
                                            placeholder="Reason for return (e.g. Damaged, Expired...)"
                                            value={returnReasons[lot.lot_id] || ''}
                                            onChange={(e) => handleReasonChange(lot.lot_id, e.target.value)}
                                            containerClassName="mb-0 mt-2"
                                            className="text-sm py-1.5"
                                            trailingIcon={<MessageSquare size={16} className="text-gray-400" />}
                                        />
                                    )}
                                </div>

                            ))}
                        </div>
                    </div>
                </Card>
            ))}

            {/* Fixed Bottom Summary Bar for Mobile Visibility */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-[0_-4px_10px_rgba(0,0,0,0.05)]">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <div>
                        <p className="text-xs text-gray-500 uppercase font-bold">Total Refund Value</p>
                        <p className="text-xl font-black text-orange-600">₦{totals.totalValue.toLocaleString()}</p>
                    </div>
                    <Button
                        onClick={handleSubmit}
                        disabled={totals.totalItems === 0 || isSubmitting}
                        variant="primary"
                        className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 px-6 py-3"
                    >
                        {isSubmitting ? (
                            <Loader2 className="animate-spin" size={20} />
                        ) : (
                            <Undo size={20} />
                        )}
                        {isSubmitting ? "Processing..." : `Process ${totals.totalItems} Items`}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ReturnToVendor;